import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import gtmService from '../utils/gtm';
import { cookie } from '../utils/cookie';

// Custom hook for GTM page view tracking
export const useGTMPageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if user has consented to analytics
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      // Track page view with GTM
      const pageTitle = document.title;
      const pagePath = location.pathname + location.search;

      gtmService.trackPageView(pagePath, pageTitle);

      // Also push to dataLayer for additional tracking
      gtmService.pushToDataLayer({
        event: 'virtual_page_view',
        page_location: window.location.href,
        page_path: pagePath,
        page_title: pageTitle
      });
    }
  }, [location]);
};

// Hook for tracking specific events
export const useGTMTracking = () => {
  const trackEvent = (eventName, parameters = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackEvent(eventName, parameters);
    }
  };

  const trackClick = (elementName, category = 'User Interaction') => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackClick(elementName, category);
    }
  };

  const trackFormSubmit = (formName, formType = 'general') => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackFormSubmit(formName, formType);
    }
  };

  // Marketing-specific tracking hooks (consent-aware)

  const trackLeadSubmission = (formType, leadData = {}) => {
    const consent = cookie.get('cookieConsent');

    // Lead submissions require analytics consent
    if (consent && consent.analytics) {
      gtmService.trackLeadFormSubmission(formType, leadData);
    }
  };

  const trackConversion = (conversionName, value = 0, additionalData = {}) => {
    const consent = cookie.get('cookieConsent');

    // Conversions require marketing consent for full attribution
    if (consent && consent.marketing) {
      gtmService.trackConversion(conversionName, value, additionalData);
    }
  };

  const trackProductInterest = (productType, productData = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackProductInterest(productType, productData);
    }
  };

  const trackFunnelStep = (funnelName, stepName, stepNumber, stepData = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackFunnelStep(funnelName, stepName, stepNumber, stepData);
    }
  };

  const trackFormInteraction = (formName, interactionType, interactionData = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackFormInteraction(formName, interactionType, interactionData);
    }
  };

  const trackCTAClick = (ctaName, ctaLocation = 'unknown', ctaData = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackCTAClick(ctaName, ctaLocation, ctaData);
    }
  };

  const trackTableView = (tableName, tableData = {}) => {
    const consent = cookie.get('cookieConsent');

    if (consent && consent.analytics) {
      gtmService.trackTableView(tableName, tableData);
    }
  };

  return {
    // Basic tracking
    trackEvent,
    trackClick,
    trackFormSubmit,

    // Business-specific tracking (original)
    trackLoanApplicationStart: gtmService.trackLoanApplicationStart,
    trackLoanApplicationComplete: gtmService.trackLoanApplicationComplete,
    trackCalculatorUse: gtmService.trackCalculatorUse,
    trackUserRegistration: gtmService.trackUserRegistration,
    trackUserLogin: gtmService.trackUserLogin,

    // Marketing-specific tracking (new)
    trackLeadSubmission,
    trackConversion,
    trackProductInterest,
    trackFunnelStep,
    trackFormInteraction,
    trackCTAClick,
    trackTableView
  };
};

export default useGTMTracking;